import { Box, Typography } from '@mui/material';
import { UserForAdmin } from 'entities/admin/Users/types';
import { format } from 'date-fns';
import { TableSortLabel } from 'shared/ui/tables/_common/ui/TableSortLabel';
import { ColorPalette } from 'shared/consts/colorPalette';
import { OptionsDropdown } from 'shared/ui/OptionsDropdown';
import { Column } from 'shared/ui/tables/_common/types';
import { getMaxValue, getInfOrValue } from 'shared/helpers/getMaxValue';
import { TableTranslationLabel } from 'shared/ui/tables/_common/ui/TableTranslationLabel';
import { capitalizeFirstLetter } from 'shared/helpers/capitalizeFirstLetter';
import { ExtensionsEnum } from 'entities/Extensions/enums';

export const UsersTableColumns: Array<
	Column<
		UserForAdmin & {
			openModal: (user: UserForAdmin) => void;
			navigateToDetails: (user: UserForAdmin) => void;
			sortFunction: (sort: 'asc' | 'desc') => void;
			sort: 'asc' | 'desc';
		}
	>
> = [
	{
		field: 'email',
		commonCellSettings: {
			style: {
				width: '400px',
				minWidth: '400px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="email" />,
		},
		bodyCellSettings: {
			prepareField: (user) => user?.email ?? '-',
		},
	},
	{
		field: 'firstName',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="first_name" />,
		},
		bodyCellSettings: {
			prepareField: (user) => user?.firstName || '-',
		},
	},
	{
		field: 'lastName',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="last_name" />,
		},
		bodyCellSettings: {
			prepareField: (user) => user?.lastName || '-',
		},
	},
	{
		field: 'businessName',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="business_name" />,
		},
		bodyCellSettings: {
			prepareField: (user) => user?.company?.name || '-',
		},
	},
	{
		field: 'registrationDate',
		commonCellSettings: {
			style: {
				width: '200px',
				minWidth: '200px',
				cursor: 'pointer',
			},
		},
		headerCellSettings: {
			renderCell: (coUser) => {
				return (
					<TableSortLabel sort={coUser?.sort} onSort={coUser?.sortFunction}>
						<TableTranslationLabel label="registration_date" />
					</TableSortLabel>
				);
			},
		},
		bodyCellSettings: {
			renderCell: (coUser) => {
				return coUser?.createdAt ? (
					<Typography variant={'body2'} component={'span'} color={ColorPalette.astronaut}>
						{format(new Date(coUser.createdAt), 'HH:mm\u00A0\u00A0\u00A0dd/MM/yyyy')}
					</Typography>
				) : (
					<Typography variant={'body2'} component={'span'} color={ColorPalette.baliHai}>
						{'--:--\u00A0\u00A0\u00A0--/--/----'}
					</Typography>
				);
			},
		},
	},
	{
		field: 'lastInvoice',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
			align: 'center',
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="subscription" />,
		},
		bodyCellSettings: {
			renderCell: (user) => {
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column' }}>
						{capitalizeFirstLetter(user?.subscription?.subscriptionType || '')}
						{/* TODO add sub status */}
						{/* <Typography variant={'overline1'} component={'span'} color={ColorPalette.baliHai}>
							({user?.subscription.status})
						</Typography> */}
					</Box>
				);
			},
		},
	},
	{
		field: 'amountOfCoUsers',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="co_users" />,
		},
		bodyCellSettings: {
			prepareField: (user) =>
				`${user?.subscription?.totalCousers}/${getMaxValue(
					user?.subscription.maxCousers || 0,
					user?.subscription?.maxFreeCousers || 0,
				)}`,
		},
	},
	{
		field: 'amountOfFiles',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="imported_files" />,
		},
		bodyCellSettings: {
			renderCell: (user) => user?.lastInvoice?.totalFiles || '-',
		},
	},
	{
		field: 'amountOfPages',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel path="shared_texts.pages_imported" />,
		},
		bodyCellSettings: {
			renderCell: (user) => user?.lastInvoice?.totalPages || '-',
		},
	},
	{
		field: 'amountOfExtraPages',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="total_extra_pages" />,
		},
		bodyCellSettings: {
			renderCell: (user) => user?.lastInvoice.totalExtraPages || '-',
		},
	},
	{
		field: 'inboundEmails',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="inbound_emails" />,
		},
		bodyCellSettings: {
			renderCell: (user) => {
				const isInboundEnabled = user?.company?.extensions?.find(
					(item) => item.extension === ExtensionsEnum.inbound_email,
				)?.isEnabled;

				return isInboundEnabled ? (
					<TableTranslationLabel label="active" />
				) : (
					<TableTranslationLabel label="not_active" />
				);
			},
		},
	},
	{
		field: 'inboundEmailsCount',
		commonCellSettings: {
			style: {
				width: '150px',
				minWidth: '150px',
			},
		},
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="inbound_emails_count" />,
		},
		bodyCellSettings: {
			renderCell: (user) => user?.inboundEmails || '--',
		},
	},
	{
		field: 'options',
		commonCellSettings: {
			style: {
				width: '100px',
				minWidth: '100px',
			},
		},
		bodyCellSettings: {
			renderCell: (user) => {
				return (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<OptionsDropdown
							iconId={'more_horizontal'}
							optionList={[
								{
									label: 'shared_texts.table_dropdown.details',
									value: 'Details info',
									action: () => {
										user?.navigateToDetails && user.navigateToDetails(user);
									},
								},
								{
									label: 'shared_texts.table_dropdown.remove',
									value: 'Remove user',
									action: () => {
										user?.openModal && user.openModal(user);
									},
								},
							]}
						/>
					</Box>
				);
			},
		},
	},
];
